import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OnboardingState, WhatsappGroup, WhatsappGroupResponseMode } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { PickerPlan, PickerPremium } from '../../../lib/plans';
import { devMockResponse } from '../../../lib/dev-mock-data';
import { ScheduledChangeResp } from './settings-shared';
import { liveWhatsappGroups } from './whatsapp-group-status';

// Every settings API call flows through react-query so the cache is the single
// source of truth: reads are shared/deduped across cards (no per-card fetch), and
// a write invalidates the exact keys it changed so the reads refresh themselves.
// Transport per endpoint is kept as it was: the recipient routes go through
// `apiFetch` (instance-auth aware), the premium routes through a credentialed
// `fetch`.
const PREMIUM_API = `${API_BASE}/api`;

/**
 * `/api/premium` reads through the mock layer first, then the SAME credentialed `fetch` as before —
 * the transport is deliberately not `apiFetch` (see the note above), so `devMockResponse` would never
 * see this route otherwise, and the dev panel's "Plan tier" row could not move the plan card.
 *
 * The one call site, on purpose: this is the second door into the mock layer (the first being
 * `apiFetch` itself), and `test/dev-tools-gated.test.ts` allowlists it by name.
 */
function premiumFetch(url: string): Promise<Response> {
  return Promise.resolve(devMockResponse(url) ?? fetch(url, { credentials: 'include' }));
}

export const settingsKeys = {
  recipient: ['settings', 'recipient'] as const,
  premium: ['settings', 'premium'] as const,
  plans: ['settings', 'plans'] as const,
  scheduledChange: ['settings', 'premium', 'scheduled-change'] as const,
  hasPurchases: ['settings', 'premium', 'has-purchases'] as const,
  whatsappGroups: ['settings', 'whatsapp-groups'] as const,
  changePasswordEligibility: ['settings', 'change-password-eligibility'] as const,
};

export interface Recipient {
  name?: string;
  // Absent until a number is verified, which is what the rail's connect destination keys off
  // (components/symphony/sidebar/hooks/use-sidebar-nav.ts). GET /api/recipient answers with
  // getMyRecipient, which returns the field unfiltered — unlike the create path, whose response
  // is narrowed to identity fields and carries no phone whatever is on file.
  phone?: string;
  // Onboarding status, consumed by the derived onboarding-entry decision (lib/use-onboarding-entry.ts).
  finishedOnboarding?: boolean;
  onboardingState?: OnboardingState;
}

// Carries the HTTP status so the delete-account flow can tell 404 (already gone)
// from a generic failure.
export class ApiError extends Error {
  constructor(public status: number) {
    super(`HTTP ${status}`);
  }
}

export function useRecipient() {
  return useQuery({
    queryKey: settingsKeys.recipient,
    queryFn: async (): Promise<Recipient | null> => {
      const res = await apiFetch(`${API_BASE}/api/recipient`, { credentials: 'include' });
      // 404 `no_recipient` is not a failure — it is "the search index hasn't caught up yet" (right after
      // signup) or "no recipient". Return a poll-able null instead of throwing, so the refetch below can
      // recover rather than latching an error.
      if (res.status === 404) return null;
      if (!res.ok) throw new ApiError(res.status);
      const data = (await res.json()) as { recipient?: Recipient };
      return data.recipient ?? null;
    },
    // The onboarding-entry decision (lib/use-onboarding-entry.ts) reads this to greet a brand-new user, and
    // it runs right after signup while getMyRecipient's search index still lags — so a momentary absent
    // recipient (or a transient error) must NOT permanently suppress the greeting. The shared QueryClient
    // sets retry:false + no refetch, which would strand exactly the just-signed-up population. Recover the
    // way the loader's waitForRecipientVisible does: retry a failed read, and poll while the recipient is
    // still absent, stopping the instant one resolves.
    retry: 2,
    retryDelay: 1000,
    // `data` is undefined for two unrelated reasons — the recipient is genuinely absent (the 404 above,
    // still catching up) and the read failed outright — and `undefined == null` cannot tell them apart.
    // Only the first is worth waiting for. A caller whose session has gone bad throws on every attempt,
    // so polling it never resolves: it just re-asks every two seconds for as long as the tab is open.
    refetchInterval: (data, query) => (query.state.status === 'error' ? false : data == null ? 2000 : false),
  });
}

export function useUpdateRecipientName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<void> => {
      const res = await apiFetch(`${API_BASE}/api/recipient`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new ApiError(res.status);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.recipient }),
  });
}

// Deletes the recipient, which cascades server-side to the metasite and all ISA
// data. No invalidation: the caller signs out on success.
export function useDeleteRecipient() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiFetch(`${API_BASE}/api/recipient`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new ApiError(res.status);
    },
  });
}

export function usePremium() {
  return useQuery({
    queryKey: settingsKeys.premium,
    queryFn: async (): Promise<PickerPremium | null> => {
      const res = await premiumFetch(`${PREMIUM_API}/premium`);
      if (!res.ok) throw new ApiError(res.status);
      const json = (await res.json()) as { premium?: PickerPremium | null };
      return json.premium ?? null;
    },
  });
}

export function usePlans() {
  return useQuery({
    queryKey: settingsKeys.plans,
    queryFn: async (): Promise<PickerPlan[]> => {
      const res = await fetch(`${PREMIUM_API}/plans`, { credentials: 'include' });
      if (!res.ok) throw new ApiError(res.status);
      const json = (await res.json()) as { plans?: PickerPlan[] };
      return json.plans ?? [];
    },
  });
}

// Only fetched when there's a paid active subscription (the only case a pending
// downgrade/cancellation can exist), mirroring the old gated load.
export function useScheduledChange(enabled: boolean) {
  return useQuery({
    queryKey: settingsKeys.scheduledChange,
    enabled,
    queryFn: async (): Promise<ScheduledChangeResp | null> => {
      const res = await fetch(`${PREMIUM_API}/premium/scheduled-change`, { credentials: 'include' });
      if (!res.ok) throw new ApiError(res.status);
      return (await res.json()) as ScheduledChangeResp;
    },
  });
}

// The caller's own WhatsApp groups, for the WhatsApp settings section. The route
// (`GET /api/whatsapp-groups`) is owner-scoped server-side; a 401 (no session) or
// 502 (upstream failure) both surface here as `error`. Only "live" groups are
// returned — DELETED / CREATE_FAILED are filtered out (see whatsapp-group-status).
export function useWhatsappGroups(): { groups: WhatsappGroup[]; loading: boolean; error: boolean } {
  const query = useQuery({
    queryKey: settingsKeys.whatsappGroups,
    queryFn: async (): Promise<WhatsappGroup[]> => {
      const res = await apiFetch(`${API_BASE}/api/whatsapp-groups`, { credentials: 'include' });
      if (!res.ok) throw new ApiError(res.status);
      const json = (await res.json()) as { groups?: WhatsappGroup[] };
      return json.groups ?? [];
    },
  });
  // Memoized on the cached payload: the filter would otherwise hand back a fresh array on every
  // render, so any consumer keying an effect or a useCallback on `groups` would never settle.
  const groups = useMemo(() => liveWhatsappGroups(query.data ?? []), [query.data]);
  return { groups, loading: query.isLoading, error: query.isError };
}

// Carries the server's guard `message` (400 { error: 'guard', message }) so the WhatsApp hub can
// show the reason verbatim (e.g. "verify your phone first"); a generic failure has no message.
export class GroupActionError extends Error {
  constructor(public status: number, message?: string) {
    super(message || `HTTP ${status}`);
  }
}

export interface CreateGroupInput {
  subject: string;
  description?: string;
}

const WHATSAPP_GROUPS_URL = `${API_BASE}/api/whatsapp-groups`;

async function whatsappGroupsRequest(method: string, body: unknown): Promise<WhatsappGroup | undefined> {
  const res = await apiFetch(WHATSAPP_GROUPS_URL, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as { group?: WhatsappGroup; message?: string };
  if (!res.ok) throw new GroupActionError(res.status, json.message);
  return json.group;
}

// Create / rename / set-response-mode / reset-invite / delete, as imperative React-Query cache edits
// (the house style — see CLAUDE.md): rename/mode/delete apply optimistically and roll back on failure;
// create and reset-invite reconcile from the server echo (the new group's id/status/inviteLink only
// exist server-side). All reconcile from the returned group so the cache tracks the server's truth.
export function useWhatsappGroupActions() {
  const qc = useQueryClient();
  const read = (): WhatsappGroup[] => qc.getQueryData<WhatsappGroup[]>(settingsKeys.whatsappGroups) ?? [];
  const write = (groups: WhatsappGroup[]) => qc.setQueryData(settingsKeys.whatsappGroups, groups);
  const replace = (updated: WhatsappGroup) => write(read().map((group) => (group.id === updated.id ? updated : group)));

  const create = async (input: CreateGroupInput): Promise<void> => {
    const group = await whatsappGroupsRequest('POST', { subject: input.subject.trim(), description: input.description?.trim() || undefined });
    if (group) write([...read(), group]);
  };

  const optimistic = async (groupId: string, patch: Partial<WhatsappGroup>, method: string, body: unknown): Promise<void> => {
    const previous = read();
    write(previous.map((group) => (group.id === groupId ? { ...group, ...patch } : group)));
    try {
      const echo = await whatsappGroupsRequest(method, body);
      if (echo) replace(echo);
    } catch (err) {
      write(previous);
      throw err;
    }
  };

  const rename = (groupId: string, subject: string): Promise<void> =>
    optimistic(groupId, { subject: subject.trim() }, 'PATCH', { groupId, subject: subject.trim() });

  const setResponseMode = (groupId: string, responseMode: WhatsappGroupResponseMode): Promise<void> =>
    optimistic(groupId, { responseMode }, 'PATCH', { groupId, responseMode });

  // Deliberately NOT optimistic: the replacement link is minted by Meta, so there is no value the
  // client could guess. The old link keeps working — and keeps being shown — until the echo lands,
  // which is also what both native apps do.
  const resetInvite = async (groupId: string): Promise<void> => {
    const echo = await whatsappGroupsRequest('PATCH', { groupId, resetInvite: true });
    if (echo) replace(echo);
  };

  const remove = async (groupId: string): Promise<void> => {
    const previous = read();
    write(previous.filter((group) => group.id !== groupId));
    try {
      await whatsappGroupsRequest('DELETE', { groupId });
    } catch (err) {
      write(previous);
      throw err;
    }
  };

  return { create, rename, setResponseMode, resetInvite, remove };
}

// The server's verdict on offering "Change password" (see lib/change-password-eligibility.ts for the
// policy and why it can't be per-login). `data === true` is the ONLY state that shows the entry point:
// while the check is in flight, and on any failure, the answer is unknown and we don't guess.
export function useChangePasswordEligibility() {
  return useQuery({
    queryKey: settingsKeys.changePasswordEligibility,
    queryFn: async (): Promise<boolean> => {
      const res = await apiFetch(`${API_BASE}/api/account/change-password-eligibility`, { credentials: 'include' });
      if (!res.ok) throw new ApiError(res.status);
      const json = (await res.json()) as { eligible?: boolean };
      if (typeof json.eligible !== 'boolean') throw new ApiError(res.status);
      return json.eligible;
    },
  });
}

/** The one failure the modal shows inline (under the current-password field) vs. everything else. */
export type ChangePasswordFailure = 'wrong_current_password' | 'failed';

export class ChangePasswordError extends Error {
  constructor(public reason: ChangePasswordFailure) {
    super(reason);
  }
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

// No cache to invalidate — a password isn't part of any read. The server resolves the user from the
// session, so nothing identifying is sent.
export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ currentPassword, newPassword }: ChangePasswordInput): Promise<void> => {
      const res = await apiFetch(`${API_BASE}/api/account/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (res.ok) return;
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ChangePasswordError(json.error === 'wrong_current_password' ? 'wrong_current_password' : 'failed');
    },
  });
}

export function useHasPurchases() {
  return useQuery({
    queryKey: settingsKeys.hasPurchases,
    queryFn: async (): Promise<boolean> => {
      const res = await fetch(`${API_BASE}/api/premium/has-purchases`, { credentials: 'include' });
      if (!res.ok) throw new ApiError(res.status);
      const json = (await res.json()) as { hasPurchases?: boolean };
      return json.hasPurchases === true;
    },
  });
}

// Settings holds no cancel/undo mutation: the account page reads the subscription and never writes
// to it. Cancelling, and undoing a scheduled cancel/downgrade, live on the packages page
// (SymphonyPricing), which calls /api/premium/cancel-subscription + cancel-scheduled-change itself.
